// src/Pages/PatientDashboard/Support.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Grid,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Paper,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  Search as SearchIcon,
  Article as ArticleIcon,
  PlayCircle as PlayCircleIcon,
  MonetizationOn as MonetizationOnIcon,
} from '@mui/icons-material';

const faqData = [
  {
    category: 'Appointments',
    questions: [
      {
        question: 'How do I book an appointment?',
        answer:
          'To book an appointment, click on the "Book Appointment" button and follow the instructions.',
      },
      {
        question: 'Can I reschedule my appointment?',
        answer:
          'Yes, you can reschedule your appointment through the "Appointments" section.',
      },
    ],
  },
  {
    category: 'Billing',
    questions: [
      {
        question: 'How can I view my invoice?',
        answer: 'Invoices are available in the "Billing and Payments" section.',
      },
      {
        question: 'What payment methods are accepted?',
        answer: 'We accept credit/debit cards, net banking, and UPI payments.',
      },
    ],
  },
];

const contactData = [
  {
    title: 'Technical Support',
    phone: '+91 98765 43210',
    email: 'support@mindwellclinic.in',
    hours: 'Mon-Fri, 9:00 AM - 6:00 PM',
    icon: <PhoneIcon />,
  },
  {
    title: 'Billing Inquiries',
    phone: '+91 12345 67890',
    email: 'billing@mindwellclinic.in',
    hours: 'Mon-Fri, 9:00 AM - 5:00 PM',
    icon: <MonetizationOnIcon />,
  },
  {
    title: 'General Questions',
    phone: '+91 98765 43211',
    email: 'info@mindwellclinic.in',
    hours: 'Mon-Fri, 8:00 AM - 7:00 PM',
    icon: <EmailIcon />,
  },
];

const resourceData = [
  {
    title: 'Understanding Anxiety',
    type: 'article',
    description:
      'An in-depth article explaining the causes and symptoms of anxiety.',
  },
  {
    title: 'Managing Depression',
    type: 'video',
    description: 'A video guide on managing depression with lifestyle changes.',
  },
];

const Support = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredResources = resourceData.filter((resource) =>
    resource.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant='h4' gutterBottom>
        Support & Resources
      </Typography>

      {/* FAQ Section */}
      <Typography variant='h6' gutterBottom>
        Frequently Asked Questions
      </Typography>
      {faqData.map((category, index) => (
        <Accordion key={index}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{category.category}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {category.questions.map((qa, idx) => (
              <Box key={idx} sx={{ marginBottom: 2 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
                  {qa.question}
                </Typography>
                <Typography variant='body2'>{qa.answer}</Typography>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Contact Information */}
      <Typography variant='h6' sx={{ marginTop: 4 }} gutterBottom>
        Contact Information
      </Typography>
      <Grid container spacing={2}>
        {contactData.map((contact, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card sx={{ display: 'flex', alignItems: 'center', padding: 2 }}>
              <Avatar sx={{ backgroundColor: '#1976d2', marginRight: 2 }}>
                {contact.icon}
              </Avatar>
              <CardContent sx={{ flex: 1 }}>
                <Typography variant='subtitle1'>{contact.title}</Typography>
                <Typography variant='body2'>
                  <PhoneIcon sx={{ fontSize: 16, marginRight: 1 }} />
                  {contact.phone}
                </Typography>
                <Typography variant='body2'>
                  <EmailIcon sx={{ fontSize: 16, marginRight: 1 }} />
                  {contact.email}
                </Typography>
                <Typography variant='body2'>
                  <AccessTimeIcon sx={{ fontSize: 16, marginRight: 1 }} />
                  {contact.hours}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Educational Resources */}
      <Typography variant='h6' sx={{ marginTop: 4 }} gutterBottom>
        Educational Resources
      </Typography>
      <Paper sx={{ padding: 2, marginBottom: 2 }}>
        <TextField
          fullWidth
          placeholder='Search resources...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>
      <List>
        {filteredResources.map((resource, index) => (
          <ListItem key={index}>
            <ListItemAvatar>
              <Avatar>
                {resource.type === 'article' ? (
                  <ArticleIcon />
                ) : (
                  <PlayCircleIcon />
                )}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={resource.title}
              secondary={resource.description}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default Support;
